import { useSelector, shallowEqual, useDispatch } from "react-redux";
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Modal } from "semantic-ui-react";
import CloseImg from "assets/images/nav/close.svg";
import PlusImg from "assets/images/nav/plus.svg";
import CheckImg from "assets/images/nav/check.svg";
import { SET_NEW_FILTERS, SET_SELECTED_DATE_FILTER } from "constants/actions";
import SurveyVersions from "reports/Audit/VersionControl";

const sentiments = [
  {
    id: "Very Negative",
    key: "Very Negative",
    name: "Very Negative",
    value: "Very Negative",
  },
  {
    id: "Slightly Negative",
    key: "Slightly Negative",
    name: "Slightly Negative",
    value: "Slightly Negative",
  },
  { id: "Neutral", key: "Neutral", name: "Neutral", value: "Neutral" },
  {
    id: "Slightly Positive",
    key: "Slightly Positive",
    name: "Slightly Positive",
    value: "Slightly Positive",
  },
  {
    id: "Very Positive",
    key: "Very Positive",
    name: "Very Positive",
    value: "Very Positive",
  },
];

function getFactors(questions) {
  const factors = questions.dimensions.map((dimension) => {
    return {
      dimension: dimension.title,
      factors: dimension.factors.map((factor) => {
        return {
          title: factor.title,
          id: factor.id - 1,
          type_of: "factor",
          dimensionIndex: questions.dimensions.indexOf(dimension),
        };
      }),
    };
  });
  return factors;
}

const Filters = ({
  outcomes,
  onSentimentFilter,
  onSelectOutcomes,
  filters,
  hideFactors = true,
  barSentiments,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSentiments, setSelectedSentiments] = useState([]);
  const [selectedOrientation, setSelectedOrientation] = useState(null);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]); // State for outcomes
  const [dimensions, setDimensions] = useState([]);
  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );
  const [selectedDate, setSelectedDate] = useState(null);

  const categoryRefs = useRef({});

  const { core_data, dates, selectedDateFilter, originalDates } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
      get_survey_structure: state.surveystructure.survey_structure,
      dates: state.audit?.dates,
      selectedDateFilter: state.audit?.selectedDateFilter,
      originalDates: state.audit?.originalDates
    }),
    shallowEqual
  );

  useEffect(() => {
    // Update selectedSentiments based on barSentiments ONLY if it is different
    if (barSentiments) {
      const newSentiments = sentiments.filter((sentiment) =>
        barSentiments.includes(sentiment.value)
      );
      setSelectedSentiments(newSentiments);
    }
  }, [barSentiments]);

  useEffect(() => {
    // Set selected date from Redux if available
    console.log("selectedDateFilter changed in Redux:", selectedDateFilter);
    if (selectedDateFilter) {
      setSelectedDate(selectedDateFilter);
    } else {
      setSelectedDate(null);
    }
  }, [selectedDateFilter]);

  const handleSelect = (item, parent) => {
    const selectedItem = {
      ...item,
      parent: parent?.id || parent?.name,
      id: item?.id === 0 ? 0 : item.id || item.key || item.value || item.name,
    };

    // --- 1. Multi-Select for Sentiment ---
    if (selectedItem.parent === "Sentiment") {
      // Check if we already have this sentiment
      const isSelected = selectedSentiments.some(
        (sent) => sent.id === selectedItem.id
      );
      let newSentiments = [];
      if (isSelected) {
        // If it's already selected, remove it
        newSentiments = selectedSentiments.filter(
          (sent) => sent.id !== selectedItem.id
        );
      } else {
        // Otherwise, add it
        newSentiments = [...selectedSentiments, selectedItem];
      }

      // The parent expects array of string-names, so map them
      onSentimentFilter && onSentimentFilter(newSentiments.map((s) => s.name));
      return;
    }

    // --- 2. Multi-Select for Outcomes (existing logic) ---
    if (selectedItem.parent === "Outcomes") {
      const isSelected = selectedOutcomes.some(
        (outcome) => outcome.id === selectedItem.id
      );
      setSelectedOutcomes((prev) =>
        isSelected
          ? prev.filter((outcome) => outcome.id !== selectedItem.id)
          : [...prev, selectedItem]
      );
      onSelectOutcomes(selectedItem);
      return;
    }

    // --- 3. Everything Else => normal selectedFilters (existing logic) ---
    const isSelected = selectedFilters.some(
      (i) => i.id === selectedItem.id && i.parent === selectedItem.parent
    );

    const updatedFilters = isSelected
      ? selectedFilters.filter(
          (i) => !(i.id === selectedItem.id && i.parent === selectedItem.parent)
        )
      : [...selectedFilters, selectedItem];

    dispatch({
      type: SET_NEW_FILTERS,
      payload: updatedFilters,
    });
  };

  // Handle date selection
  const handleDateSelect = (dateValue) => {
    
    if (dateValue === "latest") {
      // "Latest" means no filter
      setSelectedDate(null);
      dispatch({
        type: SET_SELECTED_DATE_FILTER,
        payload: null,
      });
    } else {
      // Store as ISO string for consistency
      const dateObj = new Date(dateValue);
      const formattedDate = !isNaN(dateObj.getTime()) ? dateObj.toISOString() : dateValue;
      
      setSelectedDate(formattedDate);
      dispatch({
        type: SET_SELECTED_DATE_FILTER,
        payload: formattedDate,
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Latest";
    
    // If it's already a properly formatted string, return it
    if (typeof dateString === "string" && !dateString.includes("GMT") && !dateString.includes("T")) {
      return dateString;
    }
    
    // Otherwise convert to a formatted date string
    try {
      const date = new Date(dateString);
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return String(dateString);
      }
      
      // Format with month name, day, and year
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    } catch (e) {
      console.error("Error formatting date:", e);
      return String(dateString); // Fallback to stringify
    }
  };

  useEffect(() => {
    if (core_data?.questions) {
      setDimensions(getFactors(core_data?.questions));
    }
  }, [core_data?.questions]);

  /**
   * clearAll
   * Reset everything, including multi sentiments
   */
  const clearAll = () => {
    // Clear sentiments
    setSelectedSentiments([]);
    onSentimentFilter && onSentimentFilter([]); // pass empty array up

    // Clear orientation
    setSelectedOrientation(null);

    // Clear outcomes
    setSelectedOutcomes([]);
    if (onSelectOutcomes) onSelectOutcomes([]);

    // Clear date filter
    setSelectedDate(null);
    dispatch({
      type: SET_SELECTED_DATE_FILTER,
      payload: null,
    });

    // Clear selectedFilters from redux
    dispatch({ type: SET_NEW_FILTERS, payload: [] });
  };

  useEffect(() => {
    const refs = {};

    if (outcomes) {
      refs["Outcomes"] = React.createRef();
      outcomes.forEach((outcome) => {
        refs[outcome.id] = React.createRef();
      });
    }

    refs["Sentiment"] = React.createRef();
    refs["DateFilter"] = React.createRef();
    if (core_data?.categories?.[0]?.categories) {
      core_data.categories[0].categories.forEach((category) => {
        refs[category.id] = React.createRef();
      });
    }

    // Add refs for dimensions
    dimensions.forEach((dimension) => {
      refs[dimension.dimension] = React.createRef();
    });

    categoryRefs.current = refs;
  }, [core_data?.categories, outcomes]);

  const handleCategoryClick = (categoryId) => {
    categoryRefs.current[categoryId]?.current.scrollIntoView({
      behavior: "smooth",
    });
  };

  // Use originalDates if available, otherwise fallback to dates
  const availableDates = originalDates || dates;

  return (
    <FilterComponent>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          marginBottom: "5px",
          marginTop: "5px",
        }}
      >
        {/* --- Render Chips for each selected sentiment --- */}
        {selectedSentiments.map((sentiment, i) => (
          <SelectedOption
            key={i}
            onClick={() => handleSelect(sentiment, { id: "Sentiment" })}
          >
            {sentiment.name} <IconImg src={CloseImg} />
          </SelectedOption>
        ))}

        {/* --- Render Chips for each selected outcome --- */}
        {selectedOutcomes.map((outcome, index) => (
          <SelectedOption
            key={index}
            onClick={() => handleSelect(outcome, { id: "Outcomes" })}
          >
            {outcome.name} <IconImg src={CloseImg} />
          </SelectedOption>
        ))}
        
        {/* Show selected date filter if one is selected */}
        {selectedDate && (
          <SelectedOption
            onClick={() => handleDateSelect("latest")}
          >
            Date: {formatDate(selectedDate)} <IconImg src={CloseImg} />
          </SelectedOption>
        )}
        
        {selectedFilters.map((item, index) => (
          <SelectedOption
            key={index}
            onClick={() => handleSelect(item, { id: item.parent })}
          >
            {item.name} <IconImg src={CloseImg} />
          </SelectedOption>
        ))}

        {/* The "Add Filters" button */}
        <Container onClick={() => setOpen(true)}>
          {selectedFilters.length === 0 &&
          selectedSentiments.length === 0 &&
          selectedOutcomes.length === 0 &&
          !selectedDate ? (
            <>
              <IconImg2 src={PlusImg} /> Add filters
            </>
          ) : (
            <>
              <IconImg2 src={PlusImg} /> Filters (
              {selectedFilters.length +
                selectedSentiments.length +
                selectedOutcomes.length +
                (selectedDate ? 1 : 0)}
              )
            </>
          )}
        </Container>

        {/* If anything is selected, show a "Clear All" */}
        {(selectedFilters.length > 0 ||
          selectedOutcomes.length > 0 ||
          selectedSentiments.length > 0 ||
          selectedOrientation ||
          selectedDate) && (
          <ClearAll onClick={clearAll}>Clear All</ClearAll>
        )}
      </div>

      <Modal
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        closeIcon={true}
      >
        <Modal.Header>
          <HeaderContent>
            <div>
              <ModalTitle>Filters</ModalTitle>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SurveyVersions label={true} />
            </div>
          </HeaderContent>
        </Modal.Header>
        <SearchBarContainer>
          <SearchBar
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBarContainer>
        <Content>
          <SideMenuContainer>
            <SideMenu>
              {filters?.map((filter) => {
                return (
                  <CategoryTitle
                    key={filter.id}
                    onClick={() => handleCategoryClick(filter.title)}
                  >
                    {filter.title}
                  </CategoryTitle>
                );
              })}
              <CategoryTitle>Filter by category</CategoryTitle>
              <Item
                onClick={() => handleCategoryClick("DateFilter")}
              >
                Date Filter
              </Item>

              {core_data?.categories?.[0]?.categories?.map((subCategory) => (
                <Item
                  key={subCategory.id}
                  onClick={() => handleCategoryClick(subCategory.id)}
                >
                  {subCategory.name}
                </Item>
              ))}
              {!hideFactors && (
                <>
                  <CategoryTitle>Filter by dimension</CategoryTitle>
                  {dimensions.map((dimension, index) => (
                    <Item
                      key={index}
                      onClick={() => handleCategoryClick(dimension.dimension)}
                    >
                      {dimension.dimension}
                    </Item>
                  ))}
                </>
              )}
            </SideMenu>
          </SideMenuContainer>

          <Separator />

          <MainMenuContainer>
            <MainMenu>
              {/* Date Filter Section */}
              <div ref={categoryRefs.current["DateFilter"]}>
                <Section>
                  <SectionTitle>Date Filter</SectionTitle>
                  <DateFilterSection>
                    <DateOptions>
                      <DateOption 
                        selected={selectedDate === null}
                        onClick={() => handleDateSelect("latest")}
                      >
                        {selectedDate === null && <IconImg2 src={CheckImg} />} Latest
                      </DateOption>
                      {availableDates && availableDates.map((date, index) => (
                        <DateOption
                          key={index}
                          selected={selectedDate && date && 
                            (selectedDate === date || 
                             new Date(selectedDate).getTime() === new Date(date).getTime())}
                          onClick={() => handleDateSelect(date)}
                        >
                          {(selectedDate && date && 
                            (selectedDate === date || 
                             new Date(selectedDate).getTime() === new Date(date).getTime())) && 
                             <IconImg2 src={CheckImg} />} {formatDate(date)}
                        </DateOption>
                      ))}
                    </DateOptions>
                  </DateFilterSection>
                </Section>
              </div>

              {filters && outcomes && (
                <div ref={categoryRefs.current["Outcomes"]}>
                  <Section>
                    <SectionTitle>Outcomes</SectionTitle>
                    <OutcomesSection>
                      {outcomes &&
                        outcomes
                          .filter((outcome) =>
                            outcome.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((outcome) => (
                            <OutcomeOptions key={outcome.id}>
                              <Option
                                onClick={() =>
                                  handleSelect(outcome, {
                                    id: "Outcomes",
                                    name: outcome.name,
                                  })
                                }
                                selected={selectedOutcomes.some(
                                  (item) => item.id === outcome.id
                                )}
                              >
                                {selectedOutcomes.some(
                                  (item) => item.id === outcome.id
                                ) && <IconImg2 src={CheckImg} />}{" "}
                                {outcome.name}
                              </Option>
                            </OutcomeOptions>
                          ))}
                    </OutcomesSection>
                  </Section>
                </div>
              )}

              <Section>
                {filters && (
                  <>
                    <div ref={categoryRefs.current["Sentiment"]}>
                      <Section>
                        <Title>Sentiment</Title>
                        <Options>
                          {sentiments
                            .filter((option) =>
                              option.name
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                            )
                            .map((option) => {
                              // Check if currently selected
                              const isSelected = selectedSentiments.some(
                                (sent) => sent.id === option.id
                              );
                              return (
                                <Option
                                  key={option.id}
                                  onClick={() =>
                                    handleSelect(option, {
                                      name: "Sentiment",
                                      id: "Sentiment",
                                    })
                                  }
                                  selected={isSelected}
                                >
                                  {isSelected && <IconImg2 src={CheckImg} />}{" "}
                                  {option.name}
                                </Option>
                              );
                            })}
                        </Options>
                      </Section>
                    </div>
                  </>
                )}

                {core_data?.categories?.[0]?.categories?.map((category) => (
                  <div
                    key={category.id}
                    ref={categoryRefs.current[category.id]}
                  >
                    <Title>{category.name}</Title>
                    <Options>
                      {category.options
                        ?.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                        )
                        .map((option) => (
                          <Option
                            key={option.id}
                            onClick={() => handleSelect(option, category)}
                            selected={selectedFilters.some(
                              (item) =>
                                item.id === option.id &&
                                item.parent === category.id
                            )}
                          >
                            {selectedFilters.some(
                              (item) =>
                                item.id === option.id &&
                                item.parent === category.id
                            ) && <IconImg2 src={CheckImg} />}{" "}
                            {option.name}
                          </Option>
                        ))}
                    </Options>
                  </div>
                ))}

                {/* Render dimensions and their factors */}
                {!hideFactors &&
                  dimensions.map((dimension, index) => (
                    <div
                      key={index}
                      ref={categoryRefs.current[dimension.dimension]}
                    >
                      <Title>{dimension.dimension}</Title>
                      <Options>
                        {dimension.factors
                          .filter((factor) =>
                            factor.title
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((factor) => (
                            <Option
                              key={factor.id}
                              onClick={() =>
                                handleSelect(
                                  {
                                    id: factor.id,
                                    name: factor.title,
                                    dimensionIndex: index,
                                    type_of: "factor",
                                  },
                                  {
                                    id: dimension.dimension,
                                    name: dimension.dimension,
                                  }
                                )
                              }
                              selected={selectedFilters.some(
                                (item) =>
                                  item.id === factor.id &&
                                  item.parent === dimension.dimension &&
                                  item.type_of === "factor" &&
                                  item.dimensionIndex === index
                              )}
                            >
                              {selectedFilters.some(
                                (item) =>
                                  item.id === factor.id &&
                                  item.parent === dimension.dimension &&
                                  item.type_of === "factor" &&
                                  item.dimensionIndex === index
                              ) && <IconImg2 src={CheckImg} />}{" "}
                              {factor.title}
                            </Option>
                          ))}
                      </Options>
                    </div>
                  ))}
              </Section>
            </MainMenu>
          </MainMenuContainer>
        </Content>
        <Bottom>
          <Button onClick={() => setOpen(false)}>Select</Button>
        </Bottom>
      </Modal>
    </FilterComponent>
  );
};

export default Filters;

// Styled components
const Container = styled.div`
  color: #2d70e2;
  font-family: Raleway;
  font-size: 12px;
  font-style: normal;
  line-height: 150%;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 0px;
`;

const SideMenuContainer = styled.div`
  width: 200px;
  overflow-y: auto;
  max-height: calc(100vh - 250px);

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
`;

const SideMenu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const MainMenuContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 250px);

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
`;

const Item = styled.div`
  margin-bottom: 5px;
  height: 36px;
  padding: 4px 12px 4px 27px;
  color: #666d79;
  font-family: Raleway;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const MainMenu = styled.div`
  padding: 20px;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const OutcomesSection = styled.div`
  display: flex;
  flex-direction: row;
`;

const Options = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 20px;
`;

const Option = styled.div`
  padding: 8px 16px;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 30px;
  font-family: Raleway;
  font-size: 14px;
  margin-right: 10px;
  margin-bottom: 10px;
  background-color: ${(props) => (props.selected ? "#EBF1FD" : "#fff")};
  color: ${(props) => (props.selected ? "#2D70E2" : "#2A3039")};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;

const SelectedOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const SelectedOption = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  font-family: Raleway;
  font-size: 12px;
  font-weight: 600;
  line-height: 140%;
  color: #2a3039;
  background-color: #f0f2f5;
  padding: 6px 12px;
  border-radius: 16px;
`;

const FilterComponent = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 85%;
`;

const IconImg = styled.img`
  width: 20px;
  margin-left: 3px;
`;

const IconImg2 = styled.img`
  width: 20px;
  margin-right: 4px;
  height: 20px;
`;

const Clear = styled.img`
  width: 20px;
  margin-left: 8px;
  cursor: pointer;
  margin-bottom: 0px;
  margin-right: 10px;
`;

const OutcomeOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: #2d70e2;
  border-radius: 8px;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  cursor: pointer;
`;

const Bottom = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  width: 100%;
  border-top: 1px solid #e5e5e5;
  padding: 20px;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 10px;
  
  & > div:first-child {
    flex: 0 0 auto;
  }
  
  & > div:last-child {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 auto;
  }
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #2d70e2;
  font-size: 14px;
  cursor: pointer;
`;

const SearchBarContainer = styled.div`
  padding: 10px 20px;
  background-color: #fff;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  font-size: 14px;
`;

const Separator = styled.div`
  width: 1px;
  background-color: #e5e5e5;
  margin: 0 10px;
`;

const CategoryTitle = styled.div`
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 10px;
  font-family: Raleway;
  font-size: 14px;
  cursor: pointer; /* Made it clickable */
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const ClearAll = styled.div`
  color:#F15062;
  font-weight: 600;
  border: 1px solid #F2F2F2;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 5px;
  padding: 6px 12px;
`;

const DateFilterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateOptions = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 20px;
`;

const DateOption = styled.div`
  padding: 8px 16px;
  flex-direction: row;
  align-items: flex-start;
  border-radius: 30px;
  font-family: Raleway;
  font-size: 14px;
  margin-right: 10px;
  margin-bottom: 10px;
  background-color: ${(props) => (props.selected ? "#EBF1FD" : "#fff")};
  color: ${(props) => (props.selected ? "#2D70E2" : "#2A3039")};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
`;